import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { pickRandomDocument, type SourceDocumentSummary } from "../../../api/content/sourcesApi";
import { appPaths } from "../../../shared/routing/appPaths";
import { reportUiError, toUiError, type UiError } from "../../../errors/uiError";
import type { CollectionVersionRef } from "../../../shared/references/contentReferences";
import { labelSegmentType, type SearchFilters } from "../../search";

export interface DocumentNavigation {
  readonly previousDocument: SourceDocumentSummary | null;
  readonly nextDocument: SourceDocumentSummary | null;
  readonly randomBusy: boolean;
  readonly navigationError: UiError | null;
  readonly openDocument: (document: SourceDocumentSummary) => void;
  readonly openRandomDocument: () => void;
}

/**
 * Previous, next and random document, for working through a corpus one by one. Previous and
 * next follow the listed documents; a random one is drawn from the documents the list keeps
 * (the search's `filters`, F88, F141), of which there are `totalDocumentCount`.
 */
export function useDocumentNavigation(
  corpusVersion: CollectionVersionRef,
  totalDocumentCount: number,
  listedDocuments: readonly SourceDocumentSummary[],
  currentDocumentId: string | null,
  filters: SearchFilters,
): DocumentNavigation {
  const navigate = useNavigate();
  const [randomBusy, setRandomBusy] = useState(false);
  const [randomError, setRandomError] = useState<UiError | null>(null);

  const position = listedDocuments.findIndex((document) => document.ref.documentId === currentDocumentId);
  const previousDocument = position > 0 ? (listedDocuments[position - 1] ?? null) : null;
  const nextDocument = position >= 0 ? (listedDocuments[position + 1] ?? null) : null;

  const openDocument = useCallback(
    (document: SourceDocumentSummary) => {
      void navigate(appPaths.sourceDocument(document.ref));
    },
    [navigate],
  );

  const openRandomDocument = useCallback(() => {
    setRandomBusy(true);
    setRandomError(null);
    pickRandomDocument(corpusVersion, totalDocumentCount, { attached: filters.attached, segmentType: labelSegmentType(filters) }).then(
      (document) => {
        setRandomBusy(false);
        if (document !== null) {
          openDocument(document);
        }
      },
      (caught: unknown) => {
        const error = toUiError(caught);
        reportUiError(error);
        setRandomError(error);
        setRandomBusy(false);
      },
    );
  }, [corpusVersion, openDocument, totalDocumentCount, filters]);

  return { previousDocument, nextDocument, randomBusy, navigationError: randomError, openDocument, openRandomDocument };
}
